import React from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Auth from './components/Auth/Auth';
import Home from './components/Home/Home';
import Messages from './components/Messages/Messages';
import Profile from './components/Profile/Profile';
import EditProfile from './components/Profile/EditProfile';
import Settings from './components/Settings/Settings';
import Notifications from './components/Notifications/Notifications';
import Navigation from './components/Navigation/Navigation';
import PhoneLogin from './pages/Auth/PhoneLogin';
import Otp from './pages/Auth/Otp';
import WelcomePolicy from './pages/Auth/WelcomePolicy';
import FirstName from './pages/Auth/FirstName';
import YourGender from './pages/Auth/YourGender';
import Interested from './pages/Auth/Interested';
import LookingFor from './pages/Auth/LookingFor';
import RecentPics from './pages/Auth/RecentPics';
import Location from './pages/Auth/Location';
import Chat from './pages/Chat/Chat';
import ProtectedAuthRoute from './components/Auth/ProtectedAuthRoute';
import ProtectedHomeRoute from './components/Auth/ProtectedHomeRoute';
import EncounterRoute from './components/Auth/EncounterRoute';
import { AuthProvider } from './contexts/AuthContext';
import { AuthDataProvider } from './contexts/AuthDataContext';
import Subscription from './components/Subscription/Subscription';
import HomePage from './pages/HomePage';
import PWAPrompt from './components/PWA/PWAPrompt';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import EncounterGender from './pages/Encounter/EncounterGender';
import EncounterFunnelTracker from './components/Tracking/EncounterFunnelTracker';
import PaymentSuccess from './pages/PaymentSuccess';
import useTracking from './hooks/useTracking';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

function GlobalTracking() {
  useTracking({ autoTrack: true });
  return null;
}

  /**
   * Main App component.
   *
   * Handles routing and navigation for the whole application.
   * 
   * @returns JSX.Element
   */
function App() {
  const location = useLocation();

  const hideNavigation = [
    '/auth', 
    '/edit-profile', 
    '/settings',
    '/terms',
    '/privacy',
    '/subscription',
    '/payment/success',

    '/',
    '/login',
    '/signup',
    '/auth/phonelogin',
    '/auth/otp',
    '/auth/welcome-policy',
    '/auth/first-name',
    '/auth/your-gender',
    '/auth/interested',
    '/auth/looking-for',
    '/auth/recent-pics',
    '/auth/location',
    '/chat',
  ];

  const shouldHideNavigation = hideNavigation.includes(location.pathname) || location.pathname.startsWith('/chat/') || location.pathname.startsWith('/admin/') || location.pathname.startsWith('/rencontre');

  return (
      <AuthProvider>
        <AuthDataProvider>
          <div className="app min-h-screen bg-gray-100">
            <GlobalTracking />
            <EncounterFunnelTracker />
            <Routes>
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/auth/phonelogin" element={
                <ProtectedAuthRoute>
                  <PhoneLogin />
                </ProtectedAuthRoute>
              } />
              <Route path="/auth/otp" element={
                <ProtectedAuthRoute>
                  <Otp />
                </ProtectedAuthRoute>
              } />
              <Route path="/auth/location" element={
                <ProtectedAuthRoute>
                  <Location />
                </ProtectedAuthRoute>
              } />
              <Route path="/auth/welcome-policy" element={
                <ProtectedAuthRoute>
                  <WelcomePolicy />
                </ProtectedAuthRoute>
              } />
              <Route path="/auth/first-name" element={
                <ProtectedAuthRoute>
                  <FirstName />
                </ProtectedAuthRoute>
              } />
              <Route path="/auth/your-gender" element={
                <ProtectedAuthRoute>
                  <YourGender />
                </ProtectedAuthRoute>
              } />
              <Route path="/auth/interested" element={
                <ProtectedAuthRoute>
                  <Interested />
                </ProtectedAuthRoute>
              } />
              <Route path="/auth/looking-for" element={
                <ProtectedAuthRoute>
                  <LookingFor />
                </ProtectedAuthRoute>
              } />
              <Route path="/auth/recent-pics" element={
                <ProtectedAuthRoute>
                  <RecentPics />
                </ProtectedAuthRoute>
              } />
              <Route path="/auth" element={<Auth />} />
              <Route path="/rencontre" element={<Navigate to="/rencontre/genre" replace />} />
              <Route path="/rencontre/genre" element={
                <EncounterGender />
              } />
              <Route path="/rencontre/recherche" element={
                <EncounterRoute step="recherche">
                  <Interested nextPath="/rencontre/relation" backPath="/rencontre/genre" />
                </EncounterRoute>
              } />
              <Route path="/rencontre/relation" element={
                <EncounterRoute step="relation">
                  <LookingFor nextPath="/rencontre/profil" backPath="/rencontre/recherche" />
                </EncounterRoute>
              } />
              <Route path="/rencontre/profil" element={
                <EncounterRoute step="profil">
                  <FirstName nextPath="/rencontre/inscription" backPath="/rencontre/relation" />
                </EncounterRoute>
              } />
              <Route path="/rencontre/inscription" element={
                <EncounterRoute step="inscription">
                  <SignupPage encounterFlow />
                </EncounterRoute>
              } />
              <Route path="/rencontre/regles" element={
                <EncounterRoute step="regles">
                  <WelcomePolicy nextPath="/rencontre/photos" backPath="/rencontre/inscription" />
                </EncounterRoute>
              } />
              <Route path="/rencontre/photos" element={
                <EncounterRoute step="photos">
                  <RecentPics
                    nextPath="/rencontre/localisation"
                    backPath="/rencontre/regles"
                    unauthenticatedPath="/rencontre/inscription"
                  />
                </EncounterRoute>
              } />
              <Route path="/rencontre/localisation" element={
                <EncounterRoute step="localisation">
                  <Location />
                </EncounterRoute>
              } />
              <Route path="/home" element={
                <ProtectedHomeRoute>
                  <Home />
                </ProtectedHomeRoute>
              } />
              <Route path="/messages" element={
                <ProtectedHomeRoute>
                  <Messages />
                </ProtectedHomeRoute>
              } />
              <Route path="/profile" element={
                <ProtectedHomeRoute>
                  <Profile />
                </ProtectedHomeRoute>
              } />
              <Route path="/edit-profile" element={
                <ProtectedHomeRoute>
                  <EditProfile />
                </ProtectedHomeRoute>
              } />
              <Route path="/settings" element={
                <ProtectedHomeRoute>
                  <Settings />
                </ProtectedHomeRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedHomeRoute>
                  <Notifications />
                </ProtectedHomeRoute>
              } />
              <Route path="/subscription" element={ 
                <ProtectedHomeRoute>
                  <Subscription />
                </ProtectedHomeRoute>
              } />
              <Route path="/payment/success" element={ 
                <ProtectedHomeRoute>
                  <PaymentSuccess />
                </ProtectedHomeRoute>
              } />
              <Route path="/chat/:id" element={
                <ProtectedHomeRoute>
                  <Chat />
                </ProtectedHomeRoute>
              } />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/" element={
                <ProtectedAuthRoute>
                  <HomePage />
                </ProtectedAuthRoute>
              } />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
            {!shouldHideNavigation && <Navigation />}
            <ToastContainer
              position="bottom-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            <PWAPrompt />
          </div>
        </AuthDataProvider>
      </AuthProvider>
  );
}

export default App;
